import { Building2, Copy, Globe, Mail, MapPin, Phone, User, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RoleBadge } from "@/components/badges";
import { UserAvatar } from "@/components/Avatar";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

function DetailRow({ icon: Icon, label, value, mono = false, onCopy, link = null, testId }) {
  if (!value) return null;
  return (
    <div data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="group mt-0.5 flex items-start justify-between gap-2">
        <span className={`flex items-start gap-2 text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>
          {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />}
          {link ? (
            <a href={link} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline break-all">{value}</a>
          ) : (
            <span className="break-words">{value}</span>
          )}
        </span>
        {onCopy && (
          <button onClick={onCopy} className="rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100" data-testid={`${testId}-copy`} title="Copy">
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export function CompanyCard({ party, members, open, onClose }) {
  const { t } = useTranslation();
  const c = party?.company;
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("companyCard.copied", { label }));
    } catch (e) {
      console.warn("clipboard failed", e);
    }
  };
  const copyAll = () => {
    if (!c) return;
    const lines = [c.name, c.code, c.contact_person, c.contact_email, c.contact_phone, c.address, c.website].filter(Boolean);
    copy(lines.join("\n"), t("companyCard.details"));
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="overflow-hidden rounded-2xl border-slate-200 p-0 sm:max-w-xl md:max-w-2xl" data-testid="company-card-dialog">
        <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-6 pb-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {c?.code && (
                  <span className="rounded border border-sky-400/30 bg-sky-500/20 px-2.5 py-0.5 font-mono text-xs font-bold uppercase text-sky-300" data-testid="company-card-code">{c.code}</span>
                )}
                {party?.role && <RoleBadge role={party.role} />}
              </div>
              <h2 className="font-display text-xl font-bold leading-snug tracking-tight text-white sm:text-2xl" data-testid="company-card-title">{c?.name || party?.company_name}</h2>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button variant="ghost" size="icon" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={copyAll} title={t("companyCard.copyDetails")} data-testid="company-card-copy-btn">
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={onClose} data-testid="company-card-close-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 border-b border-slate-200 bg-slate-50/50 p-6 sm:grid-cols-2">
          <DetailRow label={t("companyCard.legalTitle")} value={c?.name} testId="company-card-legal-title" />
          <DetailRow icon={User} label={t("companyCard.contactPerson")} value={c?.contact_person} testId="company-card-contact-person" />
          <DetailRow icon={Mail} label={t("companyCard.email")} value={c?.contact_email} testId="company-card-email" onCopy={() => copy(c.contact_email, t("companyCard.email"))} />
          <DetailRow icon={Phone} label={t("companyCard.phone")} value={c?.contact_phone} mono testId="company-card-phone" onCopy={() => copy(c.contact_phone, t("companyCard.phone"))} />
          <DetailRow icon={MapPin} label={t("companyCard.address")} value={c?.address} testId="company-card-address" onCopy={() => copy(c.address, t("companyCard.address"))} />
          <DetailRow icon={Globe} label={t("companyCard.website")} value={c?.website} testId="company-card-website" link={c?.website ? (c.website.startsWith("http") ? c.website : `https://${c.website}`) : null} />
        </div>

        <div className="space-y-3 p-6">
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{t("companyCard.projectTeam")}</div>
          {members.length === 0 ? (
            <p className="py-2 text-xs italic text-slate-400" data-testid="company-card-members-empty">{t("companyCard.noMembers")}</p>
          ) : (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="company-card-members-list">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5 transition-all hover:border-sky-300" data-testid={`company-card-member-${m.id}`}>
                  <UserAvatar user={m} size="h-9 w-9" textSize="text-sm" testId={`company-card-avatar-${m.id}`} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{m.name}</div>
                    <div className="truncate text-xs text-slate-500">{m.job_title || m.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-100/70 px-6 py-3.5 font-mono text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-sky-600" /> {t("companyCard.verified")}</span>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="company-card-close-footer">{t("companyCard.close")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
